import { NavArrowDown, NavArrowUp, Xmark } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from 'components/ui/sheet';
import { Switch } from 'components/ui/switch';
import { useSetCreditAccess } from 'features/credits/hooks/use-credits';
import { AddSeatDialog } from 'features/iam/components/seats/add-seat-dialog';
import { SeatPlanCell } from 'features/iam/components/seats/seat-plan-cell';
import { useGivePlan } from 'features/iam/components/seats/use-give-plan';
import { usePlanSeats } from 'features/subscription/hooks/use-seats';
import { planSeatsOfUser } from 'features/subscription/utils/seat-pools';
import { cn } from 'lib/utils';
import type { TeamsPerson } from '../teams-mock-data';
import {
  teamsPlanSelectClassName,
  teamsSwitchClassName,
  teamsSwitchThumbClassName,
} from './teams-styles';
import { TeamsUsagePanelBody } from './teams-usage-panel-body';

const panelIconButtonClassName =
  'flex size-8 items-center justify-center rounded-full text-foreground hover:bg-muted disabled:opacity-40 disabled:hover:bg-transparent';

type TeamsUsagePanelProps = {
  member: TeamsPerson | null;
  /** The range picked on the Usage tab, as yyyy-MM-dd. */
  startDate: string;
  endDate: string;
  /** Left out when there is no previous / next person on this page. */
  onPrevious?: () => void;
  onNext?: () => void;
  onClose: () => void;
};

/** Figma 10827:13126 — one person's usage, opened from a Teams → Usage row. */
export function TeamsUsagePanel({
  member,
  startDate,
  endDate,
  onPrevious,
  onNext,
  onClose,
}: TeamsUsagePanelProps) {
  const { t } = useTranslation();
  // Same plan and credit-access calls as the People tab, so a change shows on both tabs.
  const { planSeats } = usePlanSeats(true);
  const { givePlan, isPending: isMovingSeats, dialog: seatDialog } = useGivePlan(planSeats);
  const setCreditAccess = useSetCreditAccess();
  const currentPlan = member ? planSeatsOfUser(planSeats, member.id) : undefined;
  const creditAccess = member?.creditAccess ?? true;

  return (
    <Sheet open={member != null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        hideClose
        // A see-through overlay instead of hideOverlay: Radix locks page scroll from the overlay,
        // so without it the page behind scrolls and shows a second scrollbar.
        overlayClassName="bg-transparent backdrop-blur-none"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-panel"
      >
        {member && (
          <>
            <header className="flex flex-col gap-3.5 border-b border-border px-4 py-4 sm:px-6">
              <div className="flex items-center gap-3">
                <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-grey-100 text-xs font-medium leading-heading text-grey-800">
                  {member.initials}
                </div>
                <div className="flex min-w-0 flex-1 flex-col">
                  <SheetTitle className="truncate text-lg font-medium text-foreground">
                    {member.name}
                  </SheetTitle>
                  <SheetDescription className="truncate text-xs leading-heading text-muted-foreground">
                    {member.email}
                  </SheetDescription>
                </div>
                <div className="flex shrink-0 items-center gap-0.5">
                  <button
                    type="button"
                    disabled={!onPrevious}
                    onClick={onPrevious}
                    aria-label={t('TEAMS_PREVIOUS_PERSON', { defaultValue: 'Previous person' })}
                    className={panelIconButtonClassName}
                  >
                    <NavArrowUp width={18} height={18} strokeWidth={1.5} aria-hidden />
                  </button>
                  <button
                    type="button"
                    disabled={!onNext}
                    onClick={onNext}
                    aria-label={t('TEAMS_NEXT_PERSON', { defaultValue: 'Next person' })}
                    className={panelIconButtonClassName}
                  >
                    <NavArrowDown width={18} height={18} strokeWidth={1.5} aria-hidden />
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label={t('CLOSE', { defaultValue: 'Close' })}
                    className={panelIconButtonClassName}
                  >
                    <Xmark width={18} height={18} strokeWidth={1.5} aria-hidden />
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
                <SeatPlanCell
                  current={currentPlan}
                  planSeats={planSeats}
                  disabled={isMovingSeats}
                  triggerClassName={cn(
                    teamsPlanSelectClassName,
                    '!rounded-card border-solid py-0 font-normal',
                    currentPlan ? 'text-foreground' : 'text-muted-foreground'
                  )}
                  onPick={(entry) => givePlan([{ userId: member.id, name: member.name }], entry)}
                />
                <label className="flex items-center gap-2.5 text-sm leading-body text-grey-800 dark:text-grey-300">
                  {t('TEAMS_COL_CREDIT_ACCESS', { defaultValue: 'Credit access' })}
                  <Switch
                    checked={creditAccess}
                    onCheckedChange={(allowed) =>
                      setCreditAccess.mutate({ userId: member.id, allowed })
                    }
                    aria-label={t('TEAMS_CREDIT_ACCESS_FOR', {
                      defaultValue: '{{name}} can use shared credits',
                      name: member.name,
                    })}
                    className={teamsSwitchClassName}
                    thumbClassName={teamsSwitchThumbClassName}
                  />
                </label>
              </div>
            </header>

            {/* Same thin, rounded scrollbar as the settings drawer, not the browser's wide default. */}
            <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6 [scrollbar-width:thin] [scrollbar-color:hsl(var(--grey-200))_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-grey-200">
              <TeamsUsagePanelBody
                member={member}
                planName={currentPlan?.name ?? null}
                startDate={startDate}
                endDate={endDate}
              />
            </div>
          </>
        )}
      </SheetContent>
      <AddSeatDialog {...seatDialog} />
    </Sheet>
  );
}
